import {
  calcElectricityConsumtionHourSeries,
  calcNetElectricityConsumtionDensityHourSerie,
} from '../../../energy-system/electricity-consumption-hour-series-calculator'
import { BuildingEnergyModelFragment } from '../../../generated/graphql'
import { i18n } from '../../../i18n'
import {
  averageByHour,
  CalibrationHourSeriesType,
  dayHours,
  hoursOfTheDay,
  monthsSumSerie,
  yearHours,
  yearMonths,
} from './calibration-util'
import { createChartSerie, CustomLineStyles, CustomSeries } from './chart-util'

const DEFAULT_ELECTRICITY_LINETYPE = CustomLineStyles.Solid
const DEFAULT_ELECTRICITY_LINEWIDTH = 0.1

const MapTypeToCustomSeriesId: Record<CalibrationHourSeriesType, string> = {
  [CalibrationHourSeriesType.CoolingSystem]: i18n.t(
    'buildingEnergyModels.calibration.dataTypes.systemCooling',
  ),
  [CalibrationHourSeriesType.CoolingSpace]: i18n.t(
    'buildingEnergyModels.calibration.dataTypes.spaceCooling',
  ),
  [CalibrationHourSeriesType.CoolingAhu]: i18n.t(
    'buildingEnergyModels.calibration.dataTypes.ahuCooling',
  ),
  [CalibrationHourSeriesType.ElectricityBaseLoad]: i18n.t(
    'buildingEnergyModels.calibration.dataTypes.elBaseLoad',
  ),
  [CalibrationHourSeriesType.ElectricityVentilation]: i18n.t(
    'buildingEnergyModels.calibration.dataTypes.elVentilation',
  ),
  [CalibrationHourSeriesType.ElectricityAppliances]: i18n.t(
    'buildingEnergyModels.calibration.dataTypes.elAppliances',
  ),
  [CalibrationHourSeriesType.ElectricityLighting]: i18n.t(
    'buildingEnergyModels.calibration.dataTypes.elLighting',
  ),
}

export const calcSortedNetElectricityDensityInYearByHourChartData = (
  model: BuildingEnergyModelFragment,
  outsideTempHourSerie: number[],
  year: number,
): CustomSeries => {
  const id = i18n.t('buildingEnergyModels.calibration.dataTypes.netElectricity')

  let electricityDensitySerie = calcNetElectricityConsumtionDensityHourSerie(
    model,
    outsideTempHourSerie,
    model.area,
    year,
  )

  electricityDensitySerie = electricityDensitySerie.sort((a, b) => b - a)

  return createChartSerie(
    id,
    yearHours(),
    electricityDensitySerie,
    CustomLineStyles.Solid,
    4,
  )
}

export const calcSumElectricityInYearByMonthChartData = (
  model: BuildingEnergyModelFragment,
  outsideTempHourSerie: number[],
  year: number,
): CustomSeries[] => {
  const { area } = model

  const electricitySeries = calcElectricityConsumtionHourSeries(
    model,
    outsideTempHourSerie,
    area,
    year,
  )

  const monthValues = yearMonths()

  const result: CustomSeries[] = []

  for (let i = 0; i < electricitySeries.length; i++) {
    for (let j = 0; j < electricitySeries[i].serie.length; j++) {
      electricitySeries[i].serie[j] /= area
    }

    result.push(
      createChartSerie(
        MapTypeToCustomSeriesId[electricitySeries[i].type],
        monthValues,
        monthsSumSerie(electricitySeries[i].serie, year),
        DEFAULT_ELECTRICITY_LINETYPE,
        DEFAULT_ELECTRICITY_LINEWIDTH,
        true,
      ),
    )
  }

  return result
}

export const calcElectricityInYearByHourChartData = (
  model: BuildingEnergyModelFragment,
  outsideTempHourSerie: number[],
  calScheduleSerie: number[],
  year: number,
): CustomSeries[] => {
  const { area } = model

  const electricitySeries = calcElectricityConsumtionHourSeries(
    model,
    outsideTempHourSerie,
    area,
    year,
  )

  const hourValues = dayHours()
  const hours = hoursOfTheDay(0)

  const result: CustomSeries[] = []

  for (let i = 0; i < electricitySeries.length; i++) {
    for (let j = 0; j < electricitySeries[i].serie.length; j++) {
      electricitySeries[i].serie[j] /= area
    }

    result.push(
      createChartSerie(
        MapTypeToCustomSeriesId[electricitySeries[i].type],
        hourValues,
        averageByHour(
          hourValues,
          electricitySeries[i].serie,
          calScheduleSerie,
          hours,
        ),
        DEFAULT_ELECTRICITY_LINETYPE,
        DEFAULT_ELECTRICITY_LINEWIDTH,
        true,
      ),
    )
  }

  return result
}
